import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, generatePath } from 'react-router-dom'
import AddIcon from '@material-ui/icons/Add'
import PublishIcon from '@material-ui/icons/Publish'

import { PATHS } from 'Config/Routing/paths'
import { hashObject } from 'Utils/Strings'
import { isPowerBankTypeName } from 'Utils/PowerBanks'
import { getInventory } from 'Reducers/Inventory/actions'
import {
  INVENTORY_MOVEMENT_IN,
  INVENTORY_MOVEMENT_OUT,
  INVENTORY_MOVEMENT_INIT,
} from 'shared/constants'

import Progress from 'Components/Progress'
import { LinkView } from 'Components/Link/View'
import { ButtonEditInline } from 'Components/Buttons/ButtonEditInline'
import { ButtonDeleteInline } from 'Components/Buttons/ButtonDeleteInline'
import { PageWrapper } from 'Components/Layout/Page/PageWrapper'
import InfiniteScrollingTable from 'Components/InfiniteScrollingTable'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { FilterBox } from 'Components/Filter/FiltersWrapper/box'
import { FilterSelect } from 'Components/Filter/FilterSelect'
import { FiltersWrapper } from 'Components/Filter/FiltersWrapper'
import { SearchInput } from 'Components/Header/SearchInput'
import StyledIconButton from 'Components/Form/StyledIconButton'

import {
  useQuery,
  useTypeAndClientOptions,
  useTypeAndClientHashmap,
} from '../../utils'

const tableHeaders = [
  'Client',
  'Type',
  'Tag',
  'Description',
  'Artwork ID',
  'Actual QTY',
  'Total Received',
  'Actions',
]

const InventoryTable = (props, ref) => {
  const { showModal } = props
  const { cityId } = useParams()
  const query = useQuery()
  const [searchText, setSearchText] = useState()
  const { typeOptions, clientOptions } = useTypeAndClientOptions()
  const { clientsHashmap, typesHashmap } = useTypeAndClientHashmap()
  const [clientId, setClientId] = useState(query.get('Client'))
  const [typeId, setTypeId] = useState(query.get('Type'))
  const dispatch = useDispatch()

  const inventoryItems = useSelector(state => state.inventory.inventoryItems)
  const clients = useSelector(state => state.clients.clientsList)
  const searchResults = useSelector(state => state.inventory.search)
  const movements = useSelector(state => state.inventory.movements)
  const inventoryTypes = useSelector(state => state.inventory.inventoryTypes)

  const { searchInfo, searchHash } = useMemo(() => {
    const searchHash = hashObject({
      cityId,
      searchText,
      clientId,
      typeId,
    })

    return { searchInfo: searchResults[searchHash] || {}, searchHash }
  }, [searchResults, searchText, clientId, typeId])

  useEffect(() => {
    if (!searchInfo.loading) {
      onLoadMore()
    }
  }, [searchHash])

  useImperativeHandle(ref, () => ({
    reloadData: () => onLoadMore(false),
  }))

  const inventory = useMemo(() => {
    if (
      !Object.keys(clientsHashmap).length ||
      !Object.keys(typesHashmap).length
    ) {
      return []
    }

    const ids = [...new Set(searchInfo.ids)] || []

    const combineItemData = ids.reduce((acc, key) => {
      const item = inventoryItems[key]
      const itemMovements = movements[item.id].filter(
        item => item.relatedCityId === cityId,
      )

      const amountTypeMovements = itemMovements.reduce(
        (acc, item) => {
          switch (Number(item.movementTypeId)) {
            case INVENTORY_MOVEMENT_INIT:
              acc.init += item.quantity
              break
            case INVENTORY_MOVEMENT_IN: {
              acc.in += item.quantity
              break
            }
            case INVENTORY_MOVEMENT_OUT: {
              acc.out += item.quantity
              break
            }
          }
          return acc
        },
        {
          init: 0,
          in: 0,
          out: 0,
        },
      )

      const totalQuantity = amountTypeMovements.init

      //This counts because "in" and "out" always accumulate
      const activeQuantity =
        totalQuantity - (amountTypeMovements.out - amountTypeMovements.in)

      const itemWithTotalQuantity = {
        ...item,
        quantity: activeQuantity,
        initialAmount: totalQuantity,
      }

      const typeName = typesHashmap[itemWithTotalQuantity.typeId].name
      // One power bank row is one physical unit with a serial and a hold, so
      // its quantity is never edited by hand and it is never deleted here:
      // units only come in through the CSV upload and leave through the
      // pickup page.
      const isPowerBank = isPowerBankTypeName(typeName)

      const itemWithAllData = [
        clientsHashmap[itemWithTotalQuantity.clientId].name,
        typeName,
        itemWithTotalQuantity.tag,
        itemWithTotalQuantity.description,
        itemWithTotalQuantity.campaignArtworkId || '-',
        itemWithTotalQuantity.quantity,
        itemWithTotalQuantity.initialAmount,
        <div className="actions-cell">
          <LinkView
            to={generatePath(PATHS.cities.movements, {
              cityId,
              inventoryId: itemWithTotalQuantity.id,
            })}
          />
          {isPowerBank ? (
            <span
              className="power-bank-note"
              title="Power banks are one unit per row. Add them with the CSV upload and hand them over on the pickup page."
            >
              Managed via CSV upload
            </span>
          ) : (
            <>
              <ButtonEditInline
                onClickHandler={() => showModal('edit', itemWithTotalQuantity)}
              />
              <ButtonDeleteInline
                onClickHandler={() =>
                  showModal('delete', itemWithTotalQuantity)
                }
              />
            </>
          )}
        </div>,
      ]

      if (searchText || clientId || typeId) {
        acc.push(itemWithAllData)
      } else {
        if (itemWithTotalQuantity.quantity > 0) {
          acc.push(itemWithAllData)
        }
      }

      return acc
    }, [])

    return combineItemData
  }, [
    inventoryItems,
    movements,
    clientsHashmap,
    typesHashmap,
    searchText,
    clientId,
    typeId,
    searchInfo,
  ])

  const onLoadMore = useCallback(
    (isLoadNextPage = true) => {
      dispatch(
        getInventory(
          500,
          searchInfo.pageInfo && isLoadNextPage
            ? searchInfo.pageInfo.endCursor
            : null,
          {
            cityId,
            searchText,
            clientId,
            typeId,
          },
        ),
      )
    },
    [searchInfo, cityId, searchText, clientId, typeId],
  )

  return (
    <PageWrapper>
      <LayoutDashboard>
        <FiltersWrapper className="filters-wrapper">
          <FilterBox className="filter-box">
            <SearchInput
              className="search-input"
              placeholder="Search"
              searchHandler={setSearchText}
            />
          </FilterBox>
          <FilterBox dropdowns className="filter-box">
            <FilterSelect
              hasPrimaryText={false}
              label="Client"
              name="client"
              options={clientOptions}
              value={clientId}
              onChange={(e, index, value) => {
                setClientId(value)
              }}
              sorted={true}
            />
            <FilterSelect
              hasPrimaryText={false}
              label="Type"
              name="type"
              options={typeOptions}
              value={typeId}
              onChange={(e, index, value) => {
                setTypeId(value)
              }}
              sorted={true}
            />

            <StyledIconButton
              className="button-add"
              onClick={() => showModal('add')}
            >
              <AddIcon />
            </StyledIconButton>
            <StyledIconButton
              className="button-add"
              title="Import power banks from a CSV"
              aria-label="Import power banks from a CSV"
              onClick={() => showModal('importPowerBanks')}
            >
              <PublishIcon />
            </StyledIconButton>
          </FilterBox>
        </FiltersWrapper>
      </LayoutDashboard>
      {(!inventoryTypes.length || !clients.length) && <Progress />}
      {inventoryTypes.length && clients.length && (
        <InfiniteScrollingTable
          headers={tableHeaders}
          data={inventory}
          onLoadMore={onLoadMore}
          hasNextPage={searchInfo.pageInfo && searchInfo.pageInfo.hasNextPage}
          loading={searchInfo.loading}
        />
      )}
    </PageWrapper>
  )
}

export default forwardRef(InventoryTable)
